import { useEffect, useMemo, useState } from "react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@components/ui/tabs"
import { useSearchParams } from "react-router"
import { useNewOrderFunnel } from "./utils/useNewOrderFunnel"
import { ChooseOffer } from "./FunnelSteps/ChooseOffer"
import { SignUp } from "./FunnelSteps/SignUp"
import { Verification } from "./FunnelSteps/Verification"
import { Confirmation } from "./FunnelSteps/Confirmation"
import { type Step, stepOrder } from "./utils/types"
import { defineStep, type TStepResolution } from "./utils/utils"
import { useGetShopperQuery } from "@apis/shoppers/useShoppers"
import { useGetOfferQuery } from "@apis/offers/useOffers"

const NewOrderFunnelInner = () => {
  const [searchParams] = useSearchParams()
  const queryParams = useMemo(
    () => Object.fromEntries(searchParams),
    [searchParams],
  )

  const resolution = useMemo<TStepResolution>(
    () => defineStep(queryParams),
    [queryParams],
  )

  const { step, setStep, setShopper, setSelectedOffer } = useNewOrderFunnel()

  // Read shopper_guid from URL and fetch shopper when present
  const shopper_guid = queryParams?.shopper_guid as string | undefined
  const getShopperQuery = useGetShopperQuery(shopper_guid)
  const getOfferQuery = useGetOfferQuery(getShopperQuery?.data?.offer_code)

  useEffect(() => {
    if (getShopperQuery?.data) {
      setShopper(getShopperQuery.data)
    }
  }, [getShopperQuery?.data, setShopper])

  const [maxStep, setMaxStep] = useState<Step>("chooseOffer")

  if (stepOrder.indexOf(step) > stepOrder.indexOf(maxStep)) {
    setMaxStep(step)
  }

  useEffect(() => {
    if (getOfferQuery?.data) {
      setSelectedOffer(getOfferQuery.data)
      setStep("signUp")
      return
    }
    setStep(resolution.step as Step)
  }, [resolution.step, getOfferQuery?.data, setSelectedOffer, setStep])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [step])

  const canNavigateTo = (target: Step) =>
    stepOrder.indexOf(target) <= stepOrder.indexOf(maxStep)

  return (
    <Tabs
      value={step}
      onValueChange={(val) => {
        const target = val as Step
        if (canNavigateTo(target)) setStep(target)
      }}
      className="tw:w-full"
    >
      <TabsList className="tw:grid tw:w-full tw:grid-cols-4">
        <TabsTrigger
          value="chooseOffer"
          disabled={!canNavigateTo("chooseOffer")}
        >
          Choose Offer
        </TabsTrigger>
        <TabsTrigger value="signUp" disabled={!canNavigateTo("signUp")}>
          Signup
        </TabsTrigger>
        <TabsTrigger
          value="verification"
          disabled={!canNavigateTo("verification")}
        >
          Verification
        </TabsTrigger>
        <TabsTrigger
          value="confirmation"
          disabled={!canNavigateTo("confirmation")}
        >
          Confirmation
        </TabsTrigger>
      </TabsList>

      <TabsContent value="chooseOffer">
        <ChooseOffer />
      </TabsContent>
      <TabsContent value="signUp">
        <SignUp />
      </TabsContent>
      <TabsContent value="verification">
        <Verification />
      </TabsContent>
      <TabsContent value="confirmation">
        <Confirmation
          order_id_param={
            resolution.step === "confirmation" ? resolution.order_id : undefined
          }
        />
      </TabsContent>
    </Tabs>
  )
}

export const NewOrderFunnel = () => {
  return <NewOrderFunnelInner />
}
